import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css'; // Import KaTeX CSS
import blogService from '../services/BlogService';

/**
 * BlogPost Component - Individual blog post viewer
 * 
 * Features:
 * - Renders markdown content with GitHub-flavored markdown support
 * - Navigation between posts (previous/next)
 * - Responsive sidebar with all posts (on desktop)
 * - SEO-friendly with proper meta information
 * - Loading states and error handling
 */
const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  
  const [postContent, setPostContent] = useState('');
  const [postMetadata, setPostMetadata] = useState(null);
  const [navigation, setNavigation] = useState({ previous: null, next: null });
  const [allPosts, setAllPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showMobileToc, setShowMobileToc] = useState(false);
  const [tocItems, setTocItems] = useState([]);
  const [activeSection, setActiveSection] = useState('');

  // Load post data when slug changes
  useEffect(() => {
    const loadPost = async () => {
      try {
        console.log('🔍 BlogPost: Starting to load post with slug:', slug);
        setIsLoading(true);
        setError(null);

        // Get post metadata
        console.log('📄 BlogPost: Loading metadata...');
        const metadata = await blogService.getPostMetadata(slug);
        console.log('📄 BlogPost: Metadata loaded:', metadata);
        if (!metadata) {
          setError('Blog post not found');
          return;
        }

        // Load post content
        console.log('📝 BlogPost: Loading content...');
        const content = await blogService.loadPostContent(slug);
        console.log('📝 BlogPost: Content loaded, length:', content?.length);
        
        // Get navigation data
        console.log('🧭 BlogPost: Loading navigation...');
        const nav = await blogService.getPostNavigation(slug);
        console.log('🧭 BlogPost: Navigation loaded:', nav);
        
        // Get all posts for sidebar
        console.log('📚 BlogPost: Loading all posts...');
        const posts = await blogService.getAllPosts();
        console.log('📚 BlogPost: All posts loaded, count:', posts?.length);

        setPostMetadata(metadata);
        setPostContent(content);
        setNavigation(nav);
        setAllPosts(posts);

        // Update document title
        document.title = `${metadata.title} - LazyMagpie Blog`;

      } catch (err) {
        console.error('Error loading blog post:', err);
        console.error('Error details:', err.stack);
        setError(err.message || 'Failed to load blog post');
      } finally {
        setIsLoading(false);
      }
    };

    if (slug) {
      loadPost();
    }
  }, [slug]);

  // Generate Table of Contents from markdown content
  useEffect(() => {
    if (postContent) {
      const generateToc = (content) => {
        const headingRegex = /^(#{1,4})\s+(.+)$/gm;
        const headings = [];
        let match;
        
        while ((match = headingRegex.exec(content)) !== null) {
          const level = match[1].length;
          const title = match[2].trim();
          const id = title.toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '');
          
          headings.push({
            id,
            title,
            level
          });
        }
        
        return headings;
      };
      
      setTocItems(generateToc(postContent));
    }
  }, [postContent]);

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = tocItems.map(item => 
        document.getElementById(item.id)
      ).filter(Boolean);
      
      if (headingElements.length === 0) return;
      
      const scrollPosition = window.scrollY + 100; // Offset for header
      
      // Find the active section
      let activeId = '';
      for (let i = headingElements.length - 1; i >= 0; i--) {
        if (headingElements[i].offsetTop <= scrollPosition) {
          activeId = headingElements[i].id;
          break;
        }
      }
      
      setActiveSection(activeId);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Set initial active section

    return () => window.removeEventListener('scroll', handleScroll);
  }, [tocItems]);

  // Cleanup document title on unmount
  useEffect(() => {
    return () => {
      document.title = 'LazyMagpie Gaming';
    };
  }, []);

  const handlePostNavigation = (postSlug) => {
    navigate(`/blog/${postSlug}`);
    setShowSidebar(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.offsetTop - 80; // Account for header
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
    setShowMobileToc(false);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="animate-pulse">
              <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
              <div className="h-8 bg-gray-300 rounded w-3/4 mb-6"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="max-w-4xl mx-auto pt-8">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4">📝</div>
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Blog Post Not Found</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <Link
                to="/blog"
                className="inline-flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
              >
                ← Back to Blog
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="xl:flex xl:max-w-none xl:mx-0 xl:px-4 h-auto">
        {/* Left Sidebar - All Posts (Desktop XL only, mobile shows at top) */}
        <aside className={`xl:w-80 xl:flex-shrink-0 ${showSidebar ? 'fixed inset-0 z-50 xl:static xl:z-auto' : 'hidden xl:block'}`}>
          {/* Mobile Backdrop */}
          {showSidebar && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 lg:hidden"
              onClick={() => setShowSidebar(false)}
            />
          )}
          
          <div className="bg-white h-full xl:h-screen xl:sticky xl:top-0 overflow-y-auto shadow-lg xl:shadow-none">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-gray-800">All Posts</h2>
                <button
                  onClick={() => setShowSidebar(false)}
                  className="lg:hidden text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-3">
                {allPosts.map((post) => (
                  <button
                    key={post.slug}
                    onClick={() => handlePostNavigation(post.slug)}
                    className={`w-full text-left p-3 rounded-lg transition ${
                      post.slug === slug
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <h3 className={`font-medium text-sm mb-1 ${
                      post.slug === slug ? 'text-white' : 'text-gray-800'
                    }`}>
                      {post.title}
                    </h3>
                    <p className={`text-xs ${post.slug === slug ? 'text-gray-200' : 'text-gray-500'}`}>
                      {blogService.formatDate(post.date, 'short')}
                    </p>
                    {post.featured && (
                      <span className="inline-block mt-1 text-xs text-yellow-600">⭐ Featured</span>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="mt-8 pt-6 border-t border-gray-200">
                <Link
                  to="/blog"
                  className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                >
                  ← Blog Home
                </Link>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 xl:ml-6 xl:mr-6">
          <div className="lg:flex lg:gap-6">
            {/* Article Content */}
            <main className="flex-1 p-4 lg:p-8 lg:pr-4">
              <article className="bg-white rounded-lg shadow-sm">
            {/* Mobile Navigation (Top) */}
            <div className="lg:hidden bg-white border-b border-gray-200 p-4 mb-4 rounded-t-lg">
              <div className="flex items-center justify-between mb-4">
                <button
                  onClick={() => setShowSidebar(true)}
                  className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                >
                  📋 All Posts
                </button>
                
                {tocItems.length > 0 && (
                  <button
                    onClick={() => setShowMobileToc(!showMobileToc)}
                    className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                  >
                    📑 Table of Contents {showMobileToc ? '▲' : '▼'}
                  </button>
                )}
              </div>
              
              {/* Mobile ToC */}
              {showMobileToc && tocItems.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">Table of Contents</h3>
                  <div className="space-y-1">
                    {tocItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => scrollToSection(item.id)}
                        className={`block w-full text-left text-sm py-1 px-2 transition ${
                          activeSection === item.id ? 'text-black font-bold border-l-4 border-black' : 'text-gray-700 hover:text-black'
                        }`}
                        style={{ 
                          paddingLeft: `${(item.level - 1) * 12 + 8}px`,
                          fontSize: item.level === 1 ? '14px' : item.level === 2 ? '13px' : '12px',
                          fontWeight: item.level === 1 ? '600' : item.level === 2 ? '500' : '400'
                        }}
                      >
                        {item.title}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Post Header */}
            <header className="p-6 lg:p-8 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className="hidden lg:block">
                  <button
                    onClick={() => setShowSidebar(true)}
                    className="xl:hidden flex items-center px-3 py-1 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded"
                  >
                    📋 All Posts
                  </button>
                </div>
                
                <div className="hidden lg:block ml-auto">
                  <Link
                    to="/blog"
                    className="flex items-center text-sm text-gray-600 hover:text-gray-800"
                  >
                    ← Back to Blog
                  </Link>
                </div>
              </div>

              {/* Post Meta */}
              <div className="flex items-center space-x-4 mb-4">
                {postMetadata.featured && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    ⭐ Featured
                  </span>
                )}
                <time className="text-sm text-gray-500">
                  {blogService.formatDate(postMetadata.date)}
                </time>
                <span className="text-sm text-gray-500">
                  📖 {blogService.calculateReadingTime(postContent)} min read
                </span>
              </div>

              <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 mb-4">
                {postMetadata.title}
              </h1>

              <p className="text-lg text-gray-600 mb-6">
                {postMetadata.description}
              </p>

              {/* Tags */}
              {postMetadata.tags && postMetadata.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {postMetadata.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </header>

            {/* Post Content */}
            <div className="p-6 lg:p-8">
              <div className="prose prose-lg max-w-none text-left
                prose-headings:text-gray-800 prose-headings:text-left
                prose-p:text-gray-700 prose-p:leading-relaxed prose-p:text-left
                prose-a:text-gray-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-gray-800 prose-strong:font-semibold
                prose-ul:text-gray-700 prose-ol:text-gray-700
                prose-li:text-left
                prose-blockquote:border-l-gray-500 prose-blockquote:bg-gray-50 prose-blockquote:p-4 prose-blockquote:rounded-r prose-blockquote:text-left
                prose-code:text-gray-600 prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded
                prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:text-left
                prose-hr:border-gray-300
              ">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeKatex]}
                  components={{
                    // Custom components for better styling with IDs for ToC navigation
                    h1: ({children}) => {
                      const text = typeof children === 'string' ? children : children?.toString() || '';
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                      return <h1 id={id} className="text-3xl font-bold text-gray-800 mb-6 mt-8 first:mt-0 text-left scroll-mt-20">{children}</h1>;
                    },
                    h2: ({children}) => {
                      const text = typeof children === 'string' ? children : children?.toString() || '';
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                      return <h2 id={id} className="text-2xl font-bold text-gray-800 mb-4 mt-8 text-left scroll-mt-20">{children}</h2>;
                    },
                    h3: ({children}) => {
                      const text = typeof children === 'string' ? children : children?.toString() || '';
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                      return <h3 id={id} className="text-xl font-bold text-gray-800 mb-3 mt-6 text-left scroll-mt-20">{children}</h3>;
                    },
                    h4: ({children}) => {
                      const text = typeof children === 'string' ? children : children?.toString() || '';
                      const id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                      return <h4 id={id} className="text-lg font-bold text-gray-800 mb-2 mt-4 text-left scroll-mt-20">{children}</h4>;
                    },
                    p: ({children}) => <p className="text-gray-700 leading-relaxed mb-4 text-left">{children}</p>,
                    ul: ({children}) => <ul className="text-gray-700 mb-4 ml-6 list-disc text-left">{children}</ul>,
                    ol: ({children}) => <ol className="text-gray-700 mb-4 ml-6 list-decimal text-left">{children}</ol>,
                    li: ({children}) => <li className="mb-1 text-left">{children}</li>,
                    blockquote: ({children}) => (
                      <blockquote className="border-l-4 border-gray-500 bg-gray-50 p-4 my-6 rounded-r text-left">
                        {children}
                      </blockquote>
                    ),
                    code: ({inline, children}) => {
                      if (inline) {
                        return <code className="text-gray-600 bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">{children}</code>;
                      }
                      return <code className="block bg-gray-900 text-gray-100 p-4 rounded text-sm font-mono overflow-x-auto text-left">{children}</code>;
                    }
                  }}
                >
                  {postContent}
                </ReactMarkdown>
              </div>
            </div>

            {/* Post Navigation */}
            {(navigation.previous || navigation.next) && (
              <footer className="p-6 lg:p-8 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  {navigation.previous ? (
                    <button
                      onClick={() => handlePostNavigation(navigation.previous.slug)}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 group"
                    >
                      <span className="group-hover:translate-x-1 transition-transform">←</span>
                      <div className="text-left">
                        <div className="text-xs text-gray-500">Previous</div>
                        <div className="font-medium">{navigation.previous.title}</div>
                      </div>
                    </button>
                  ) : (
                    <div></div>
                  )}

                  {navigation.next ? (
                    <button
                      onClick={() => handlePostNavigation(navigation.next.slug)}
                      className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 group text-right"
                    >
                      <div>
                        <div className="text-xs text-gray-500">Next</div>
                        <div className="font-medium">{navigation.next.title}</div>
                      </div>
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </button>
                  ) : (
                    <div></div>
                  )}
                </div>
              </footer>
            )}
          </article>
        </main>

            {/* Right Sidebar - Table of Contents (Desktop LG+ only) */}
            {tocItems.length > 0 && (
              <aside className="hidden lg:block lg:w-80 lg:flex-shrink-0 lg:pl-4">
                <div className="bg-white h-full xl:h-screen xl:sticky xl:top-0 overflow-y-auto shadow-lg xl:shadow-none">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold text-gray-800">📑 Table of Contents</h2>
                    </div>
                    
                    <div className="space-y-1">
                      {tocItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => scrollToSection(item.id)}
                          className={`w-full text-left py-1 px-2 rounded transition bg-white ${
                            activeSection === item.id
                              ? 'bg-gray-100 border-l-4 border-gray-800 font-semibold'
                              : 'hover:bg-gray-50'
                          }`}
                          style={{ 
                            paddingLeft: `${(item.level - 1) * 16 + 12}px`,
                            fontSize: item.level === 1 ? '14px' : item.level === 2 ? '13px' : '12px',
                            fontWeight: item.level === 1 ? '600' : item.level === 2 ? '500' : '400'
                          }}
                        >
                          <div className={activeSection === item.id ? 'text-gray-900' : 'text-gray-700'}>
                            {item.title}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;